import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function LessonsList() {
  const [lessons, setLessons] = useState([]);
  useEffect(() => { api.get("/lessons").then(({data}) => setLessons(data.lessons)); }, []);
  return (
    <div className="space-y-6">
      <header><h1 className="font-display font-extrabold text-3xl text-slate-900">Learn</h1>
        <p className="text-slate-600 mt-1">Short lessons for the situations you actually face.</p></header>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {lessons.map((l) => (
          <Link key={l.id} to={`/learn/${l.id}`} className="rle-card p-5 block" data-testid={`lesson-card-${l.id}`}>
            <div className="flex items-center justify-between">
              <span className="rle-eyebrow">{l.topic} · {l.level.replace("_"," ")}</span>
              {l.premium && <Lock size={14} className="text-amber-600"/>}
            </div>
            <h3 className="mt-2 font-display font-semibold text-slate-900">{l.title}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{l.summary}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function LessonDetail() {
  const { lessonId } = useParams(); const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  useEffect(() => { api.get(`/lessons/${lessonId}`).then(({data}) => setLesson(data.lesson)); }, [lessonId]);
  const complete = async () => {
    await api.post("/lessons/progress", { lesson_id: lessonId, completed: true });
    toast.success("Lesson completed!");
    nav("/learn");
  };
  if (!lesson) return <div className="text-slate-500">Loading…</div>;
  if (lesson.locked) {
    return (
      <div className="max-w-2xl">
        <div className="rle-card p-8 text-center">
          <Lock size={28} className="mx-auto text-amber-600"/>
          <h1 className="mt-3 font-display font-bold text-2xl text-slate-900">This lesson is part of paid plans</h1>
          <p className="text-slate-600 mt-2">Upgrade to Essential or Complete to access the full lesson library.</p>
          <Link to="/pricing"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-4" data-testid="lesson-upgrade-btn">See Pricing</Button></Link>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-3xl">
      <span className="rle-eyebrow">{lesson.topic} · {lesson.level?.replace("_"," ")}</span>
      <h1 className="mt-2 font-display font-extrabold text-3xl text-slate-900">{lesson.title}</h1>
      <p className="text-slate-600 mt-2">{lesson.summary}</p>
      <div className="mt-8 space-y-4">
        {(lesson.sections || []).map((s, i) => (
          <div key={i} className="rle-card p-5" data-testid={`lesson-section-${i}`}>
            <div className="rle-eyebrow">{s.type}</div>
            <h3 className="mt-1 font-display font-bold text-lg text-slate-900">{s.title}</h3>
            {s.body && <p className="mt-2 text-slate-700 italic">{s.body}</p>}
            {s.items && (
              <ul className="mt-3 space-y-2">
                {s.items.map((it, j) => (
                  <li key={j} className="text-sm">
                    <span className="font-semibold text-slate-900">{it.term}</span>
                    {it.meaning && <span className="text-slate-600"> — {it.meaning}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      <div className="mt-8"><Button onClick={complete} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="lesson-complete-btn"><CheckCircle2 size={16} className="mr-1"/>Mark as complete</Button></div>
    </div>
  );
}
